import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Users, Plus } from 'lucide-react';
import BackOfficeMenu from '../components/BackOfficeMenu';
import './GestionRH.css';

// Import sub-components
import DashboardRH from './dashboard/DashboardRH';
import EmployesManagement from './employes/EmployesManagement';
import DepartementsManagement from './departements/DepartementsManagement';
import PostesManagement from './postes/PostesManagement';
import DemandesManagement from './demandes/DemandesManagement';
import EvaluationsManagement from './evaluations/EvaluationsManagement';
import SalairesManagement from './salaires/SalairesManagement';
import DocumentsRH from './documents/DocumentsRH';

export default function GestionRH({ onNavigate, onLogout, currentPage }) {
  const [activeTab, setActiveTab] = useState('Tableau de Bord');
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      const tabMap = {
        'dashboard': 'Tableau de Bord',
        'employes': 'Employés',
        'departements': 'Départements',
        'postes': 'Postes',
        'conges': 'Demandes de Congés',
        'absences': 'Absences', // Note: Demandes handles all absences usually
        'performances': 'Performances',
        'paie': 'Paie & Salaires',
        'documents-rh': 'Documents RH'
      };
      if (tabMap[tab]) setActiveTab(tabMap[tab]);
    }
  }, [location.search]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Tableau de Bord': return <DashboardRH onNavigate={onNavigate} />;
      case 'Employés': return <EmployesManagement onNavigate={onNavigate} />;
      case 'Départements': return <DepartementsManagement />;
      case 'Postes': return <PostesManagement />;
      case 'Demandes de Congés': 
      case 'Absences': return <DemandesManagement />;
      case 'Performances': return <EvaluationsManagement />;
      case 'Paie & Salaires': return <SalairesManagement onNavigate={onNavigate} />;
      case 'Documents RH': return <DocumentsRH />;
      case 'Organigramme':
        return (
          <div className="rh-table-card animate-fade-in" style={{ textAlign: 'center', padding: '40px' }}>
            <h2 style={{ color: 'var(--rh-blue-dark)' }}>Structure de l'Entreprise</h2>
            <div className="rh-org-tree" style={{ marginTop: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '25px' }}>
              <div className="rh-employee-avatar-small" style={{ width: '80px', height: '80px', fontSize: '24px', background: 'var(--rh-blue-gradient)' }}>DG</div>
              <div style={{ width: '2px', height: '40px', background: 'var(--rh-border)' }} />
              <div style={{ display: 'flex', gap: '60px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                  <div className="rh-avatar" style={{ background: '#3182CE', width: '50px', height: '50px' }}>RH</div>
                  <span style={{ fontSize: '13px', fontWeight: '700' }}>Département RH</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                  <div className="rh-avatar" style={{ background: '#38A169', width: '50px', height: '50px' }}>OP</div>
                  <span style={{ fontSize: '13px', fontWeight: '700' }}>Opérations</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                  <div className="rh-avatar" style={{ background: '#EA580C', width: '50px', height: '50px' }}>CO</div>
                  <span style={{ fontSize: '13px', fontWeight: '700' }}>Commercial</span>
                </div>
              </div>
            </div>
          </div>
        );
      default: return <DashboardRH onNavigate={onNavigate} />;
    }
  };

  return (
    <div className="rh-dashboard-container">
      <BackOfficeMenu 
        onNavigate={onNavigate} 
        onLogout={onLogout} 
        currentPage={currentPage} 
      />

      <div className="rh-dashboard-content">
        {/* Hero Section */}
        <section className="rh-hero">
          <div className="rh-hero-info">
            <div className="rh-hero-icon">
              <Users color="white" size={40} />
            </div>
            <div className="rh-hero-text">
              <h1>Gestion des Ressources Humaines</h1>
              <p>Gérez vos collaborateurs, la performance et la paie en un seul endroit</p>
            </div>
          </div>
          {activeTab === 'Employés' && (
            <button className="rh-add-btn" onClick={() => {
              // This is handled inside the component now, but we could trigger it via ref if needed
              // For now, the add button is also inside the component for better isolation
            }}>
              <Plus size={20} />
              Nouveau Collaborateur
            </button>
          )}
        </section>

        {/* Tabs Navigation */}
        <div className="rh-tabs">
          {[
            'Tableau de Bord', 
            'Employés', 
            'Départements', 
            'Postes', 
            'Demandes de Congés', 
            'Performances', 
            'Paie & Salaires', 
            'Documents RH',
            'Organigramme'
          ].map(tab => (
            <button 
              key={tab} 
              className={`rh-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Main Content Area */}
        <div className="rh-content-area">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}
